import { type App, type Component, createSSRApp, defineAsyncComponent, hydrateOnInteraction } from 'vue'
import { type Router } from 'vue-router'
import { createPinia, type Pinia } from 'pinia'
import DesignSystem from 'sisu/index'
import { parse } from 'devalue'
import { Rogue } from './js/services/Rogue'
import SpaApp from './js/app.vue'
import { date } from '@/js/helpers/date'
import Loading from '@/js/components/loading.vue'
import RogueLink from '@/js/components/rogue-link.vue'
import Price from '@/js/components/price.vue'

import 'Src/scss/base.scss'
import 'sisu/styles/styles.scss'

function initApp(app: App) {
  app.component('RogueLink', RogueLink)
  app.use(DesignSystem)

  app.component('Err404', defineAsyncComponent(() => import('./js/pages/404/404.vue')))

  // Shared (global) Components
  app.component('Loading', Loading)
  app.component('Price', Price)
  app.component('Breadcrumbs', defineAsyncComponent(() => import('./js/components/breadcrumbs.vue')))
  app.component('DrawerContents', defineAsyncComponent(() => import('./js/components/drawer-contents.vue')))
  app.component('RogueRelated', defineAsyncComponent(() => import('@/js/components/rhpa/rogue-related.vue')))
  app.component('Hotdeal', defineAsyncComponent(() => import('@/js/components/hotdeal/hotdeal.vue')))

  // Hydrated on the first click rather than at load: Vue attaches a listener to the SSR
  // markup and replays the click once the chunk lands, so only someone who opens the chat
  // pays for the module. The strategy applies while hydrating - a client-rendered page
  // loads it like any other async component.
  app.component('LiveChat', defineAsyncComponent({
    loader  : () => import('@/js/components/live-chat.vue'),
    hydrate : hydrateOnInteraction('click'),
  }))

  app.component('Cms', defineAsyncComponent(() => import('./js/pages/cms/cms.vue')))

  app.component('AdvancedProductList', defineAsyncComponent(() => import('./js/components/advanced-product-list.vue')))

  // Async component needed by Roguebuilder renderblock
  app.component('ProductSummary', defineAsyncComponent(() => import('./js/pages/product/product-summary.vue')))
  app.component('QuickBuy', defineAsyncComponent(() => import('./js/components/quick-buy.vue')))
  app.component('FeaturedIndex', defineAsyncComponent(() => import('./js/components/FeaturedIndex.vue')))
  app.component('MbnBanner', defineAsyncComponent(() => import('./js/components/banners/mbn-banner.vue')))
  app.component('Instructions', defineAsyncComponent(() => import('./js/pages/product/components/instructions.vue')))
  app.component('GiftCardBalance', defineAsyncComponent(() => import('./js/pages/product/components/balance-check-drawer.vue')))
  app.component('PrizePurse', defineAsyncComponent(() => import('./js/components/events/prize-purse.vue')))
  app.component('PrizePurseBreakdown', defineAsyncComponent(() => import('./js/components/events/prize-purse-breakdown.vue')))
  app.component('EventRegister', defineAsyncComponent(() => import('./js/components/rhpa/EventRegister.vue')))
  app.component('MultiChallengeSnipe', defineAsyncComponent(() => import('./js/components/banners/MultiChallengeSnipe.vue')))
  app.component('EmbeddedPdfList', defineAsyncComponent(() => import('./js/components/utilities/EmbeddedPdfList.vue')))
  app.component('EmbeddedPdf', defineAsyncComponent(() => import('./js/components/utilities/EmbeddedPdf.vue')))
  app.component('StrongestVueLeaderboard', defineAsyncComponent(() => import('./js/components/events/strongest-vue-leaderboard.vue')))

  // ChallengeLeaderboard is the correct name for this component, but we have a lot of legacy uses of BTWBLeaderboard and BTWBLeaderboardNew in RB
  const challengeLeaderboard = defineAsyncComponent(() => import('./js/components/events/challenge-leaderboard.vue'))
  app.component('BTWBLeaderboard', challengeLeaderboard)
  app.component('BTWBLeaderboardNew', challengeLeaderboard)
  app.component('ChallengeLeaderboard', challengeLeaderboard)
  
  app.component('ApplePay', defineAsyncComponent(() => import('./js/components/payment/ApplePay.vue')))
  app.component('GooglePay', defineAsyncComponent(() => import('./js/components/payment/GooglePay.vue')))

  // Order Item
  app.component('OrderItem', defineAsyncComponent(() => import('./js/components/cart/order-item.vue')))
  app.component('CustomOrderItem', defineAsyncComponent(() => import('./js/components/cart/custom-order-item.vue')))
  app.component('OrderItemMassStorageAddon', defineAsyncComponent(() => import('./js/components/cart/order-item-mass-storage-addon.vue')))
  app.component('OrderItemCaveAddon', defineAsyncComponent(() => import('./js/components/cart/order-item-cave-addon.vue')))
  app.component('OrderItemSlingerXAddon', defineAsyncComponent(() => import('./js/components/cart/order-item-slinger-x-addon.vue')))
  app.component('OrderItemHybridRigAddon', defineAsyncComponent(() => import('./js/components/cart/order-item-hybrid-rig-addon.vue')))
  app.component('ConfigureBuyRequest', defineAsyncComponent(() => import('./js/components/rhpa/configure-buy-request.vue')))
  app.component('Configuration', defineAsyncComponent(() => import('./js/components/rhpa/configuration.vue')))

  // Registration
  app.component('Login', defineAsyncComponent(() => import('./js/components/login/login.vue')))
  app.component('Register', defineAsyncComponent(() => import('./js/components/login/register.vue')))
  app.component('NewsletterForm', defineAsyncComponent(() => import('./js/components/login/components/newsletter-form.vue')))

  // Address Form
  app.component('AddressBook', defineAsyncComponent(() => import('./js/components/address/address-book.vue')))
  app.component('AddressForm', defineAsyncComponent(() => import('./js/components/address/address-form.vue')))
  app.component('AddressText', defineAsyncComponent(() => import('./js/components/address/address-text.vue')))
  app.component('AddressSummary', defineAsyncComponent(() => import('./js/components/address/address-summary.vue')))
  app.component('Country', defineAsyncComponent(() => import('./js/components/address/country.vue')))
  app.component('City', defineAsyncComponent(() => import('./js/components/address/city.vue')))
  app.component('Region', defineAsyncComponent(() => import('./js/components/address/region.vue')))
  app.component('Postcode', defineAsyncComponent(() => import('./js/components/address/postcode.vue')))
  app.component('AddressVerify', defineAsyncComponent(() => import('./js/components/address/address-verify.vue')))

  app.component('Rackbuilder', defineAsyncComponent(() => import('@/js/pages/product/rackbuilder/components/rackbuilder.vue')))

  app.config.globalProperties.$date = date
}

export type AppResult = {
  pinia: Pinia;
  app: App<Element>;
}

export function createApp(rogue:Rogue, router: Router, root: Component = SpaApp, initialState?: { pinia: string}): AppResult {
  const pinia = createPinia()

  if (initialState && initialState.pinia) {
    pinia.state.value = parse(initialState.pinia)
  }

  rogue.setPinia(pinia)
  rogue.setRouter(router)

  const app = createSSRApp(root)

  app.config.globalProperties.$rogue = rogue
  app.config.globalProperties.__ = rogue.__.bind(rogue)

  app.use(pinia)
  app.use(router)

  initApp(app)

  return {
    app,
    pinia,
  }
}
